import React from 'react';
import { useNavigate } from 'react-router-dom';
import { PageLayout } from '@/shared/layout';
import { Button, Filter, Search, KPICard, Card, Badge, Table } from '@/components/ui';
import { useConstructionWorkloads } from './hooks/useWorkload';
import { useAuth } from '@/core/auth/AuthContext';
import { AuthButton } from '@/core/permissions/AuthButton';
import type { WorkloadStatus } from './types/construction-workload.types';

const statusColors: Record<WorkloadStatus, string> = {
  draft: 'bg-slate-100 text-slate-600',
  calculated: 'bg-blue-100 text-blue-600',
  reviewing: 'bg-amber-100 text-amber-600',
  approved: 'bg-emerald-100 text-emerald-600',
  locked: 'bg-slate-900 text-white',
  archived: 'bg-slate-400 text-white',
};

const statusLabels: Record<WorkloadStatus, string> = {
  draft: 'Bản thảo',
  calculated: 'Đã tính toán',
  reviewing: 'Đang soát xét',
  approved: 'Đã phê duyệt',
  locked: 'Đã khóa',
  archived: 'Lưu trữ',
};

export default function WorkloadListPage() {
  const { profile } = useAuth();
  const companyId = profile?.company_id || '';
  const navigate = useNavigate();

  const {
    workloads,
    isLoading,
    filters,
    setFilters,
  } = useConstructionWorkloads(companyId);

  return (
    <PageLayout
      title="Khối lượng thi công (Workload)"
      description="Tính toán và quản lý nhân công, thiết bị và vật tư cần thiết dựa trên định mức bóc tách từ BOQ."
      breadcrumbs={[{ label: 'Thi công' }, { label: 'Workload' }]}
      isLoading={isLoading}
      headerActions={
        <div className="flex gap-2">
          <Button variant="outline" icon="ri-settings-4-line" onClick={() => navigate('./rules')}>Định mức thi công</Button>
          <AuthButton
            module="construction"
            action="create"
            variant="primary"
            icon="ri-calculator-line"
            onClick={() => navigate('./create')}
          >
            Tính toán Workload
          </AuthButton>
        </div>
      }
    >
      <div className="space-y-6">
        <PageLayout.Toolbar>
          <div className="flex flex-col md:flex-row gap-4 w-full justify-between items-start md:items-center">
            <div className="flex flex-wrap gap-3 flex-1">
              <Search
                placeholder="Tìm mã Workload, tên dự án..."
                value={filters.search}
                onChange={(v) => setFilters({ ...filters, search: v })}
                className="w-full md:w-80"
              />
              <Filter
                options={[
                  {
                    id: 'status', label: 'Trạng thái', type: 'select', choices: [
                      { value: 'all', label: 'Tất cả trạng thái' },
                      ...Object.entries(statusLabels).map(([val, label]) => ({ value: val, label }))
                    ]
                  }
                ]}
                onFilterChange={(k, v) => setFilters({ ...filters, [k]: v })}
              />
            </div>
          </div>
        </PageLayout.Toolbar>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {workloads.map((item) => (
            <Card key={item.id} className="p-6 hover:shadow-md transition-all group border-slate-100" onClick={() => navigate(`./detail/${item.id}`)}>
              <div className="flex justify-between items-start mb-4">
                <div className="flex flex-col">
                  <span className="font-mono text-[10px] font-bold text-os-primary  tracking-widest">#{item.workload_code}</span>
                  <h3 className="font-bold text-slate-900 mt-1 line-clamp-1">{item.name}</h3>
                </div>
                <Badge className={`${statusColors[item.status_code]} border-transparent text-[9px] font-bold `}>
                  {statusLabels[item.status_code]}
                </Badge>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="flex flex-col">
                  <span className="text-[10px] text-slate-400 font-bold ">Nhân công</span>
                  <span className="text-sm font-bold text-slate-900">{item.estimated_man_hours.toLocaleString()} h</span>
                </div>
                <div className="flex flex-col">
                  <span className="text-[10px] text-slate-400 font-bold ">Thiết bị</span>
                  <span className="text-sm font-bold text-slate-900">{item.estimated_equipment_hours.toLocaleString()} h</span>
                </div>
                <div className="flex flex-col">
                  <span className="text-[10px] text-slate-400 font-bold ">Vật tư dự kiến</span>
                  <span className="text-sm font-bold text-os-primary">{new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' }).format(item.estimated_material_cost)}</span>
                </div>
                <div className="flex flex-col">
                  <span className="text-[10px] text-slate-400 font-bold ">Đội thi công</span>
                  <span className="text-sm font-bold text-slate-900">{item.estimated_team_count} tổ</span>
                </div>
              </div>

              <div className="space-y-2 mb-6">
                <div className="flex items-center gap-2 text-[10px] text-slate-500 font-bold ">
                  <i className="ri-map-pin-line" />
                  {item.zone?.name || 'Toàn dự án'}
                </div>
                <div className="flex items-center gap-2 text-[10px] text-slate-500 font-bold ">
                  <i className="ri-calendar-line" />
                  {item.phase?.name || 'Mọi giai đoạn'}
                </div>
              </div>

              <div className="pt-4 border-t border-slate-50 flex justify-between items-center">
                <span className="text-[10px] text-slate-400 font-bold  italic">Cập nhật: {new Date(item.updated_at).toLocaleDateString('vi-VN')}</span>
                <Button variant="ghost" size="sm" icon="ri-arrow-right-line" className="text-os-primary font-bold">Chi tiết</Button>
              </div>
            </Card>
          ))}

          <button className="flex flex-col items-center justify-center p-6 border-2 border-dashed border-slate-200 rounded-3xl hover:border-os-primary hover:bg-os-primary/5 transition-all group min-h-[250px]">
            <div className="w-10 h-10 rounded-full bg-slate-50 flex items-center justify-center text-slate-300 group-hover:bg-os-primary/10 group-hover:text-os-primary transition-all mb-4">
              <i className="ri-add-line text-xl" />
            </div>
            <span className="text-xs font-bold text-slate-400 group-hover:text-os-primary">Khởi tạo Workload mới</span>
          </button>
        </div>
      </div>
    </PageLayout>
  );
}
